import React, { useState, useEffect } from 'react';
import { X, Input, Label, Textarea, Checkbox, Badge, AlertCircle, ExternalLink, Plus, Trash2, Edit } from 'lucide-react';
import { Button as UIButton } from './ui/button';
import { Input as UIInput } from './ui/input';
import { Label as UILabel } from './ui/label';
import { Textarea as UITextarea } from './ui/textarea';
import { Checkbox as UICheckbox } from './ui/checkbox';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';


const API = "/api";

const SharePointManagementModal = ({ product, isOpen, onClose, onUpdate }) => {
  const [sharepointLinks, setSharepointLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [formData, setFormData] = useState({
    sharepoint_url: '',
    display_name: '',
    description: '',
    is_default: false
  });
  const [errors, setErrors] = useState({});
  const { toast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    if (isOpen && product) {
      fetchSharepointLinks();
    }
  }, [isOpen, product]);

  const fetchSharepointLinks = async () => {
    if (!product) return;
    
    try {
      const response = await axios.get(`${API}/products/${product.id}/sharepoint`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('exled_token')}`
        }
      });
      setSharepointLinks(response.data);
    } catch (error) {
      console.error('Failed to fetch SharePoint links:', error);
      toast({
        title: "Error",
        description: "Failed to load SharePoint links",
        variant: "destructive",
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.sharepoint_url.trim()) {
      newErrors.sharepoint_url = 'SharePoint URL is required';
    } else if (!isValidUrl(formData.sharepoint_url)) {
      newErrors.sharepoint_url = 'Please enter a valid URL';
    }
    
    if (!formData.display_name.trim()) {
      newErrors.display_name = 'Display name is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      if (editingLink) {
        // Update existing link
        await axios.put(
          `${API}/products/${product.id}/sharepoint/${editingLink.id}`,
          formData,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('exled_token')}`
            }
          }
        );
        toast({
          title: "Success",
          description: "SharePoint link updated successfully",
        });
      } else {
        // Create new link
        await axios.post(
          `${API}/products/${product.id}/sharepoint`,
          {
            ...formData,
            product_id: product.id
          },
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('exled_token')}`
            }
          }
        );
        toast({
          title: "Success",
          description: "SharePoint link added successfully",
        });
      }
      
      // Reset form and refresh links
      setFormData({
        sharepoint_url: '',
        display_name: '',
        description: '',
        is_default: false
      });
      setEditingLink(null);
      setShowAddForm(false);
      fetchSharepointLinks();
      onUpdate(); // Refresh parent component
      
    } catch (error) {
      toast({
        title: "Error",
        description: editingLink ? "Failed to update SharePoint link" : "Failed to create SharePoint link",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (link) => {
    setEditingLink(link);
    setFormData({
      sharepoint_url: link.sharepoint_url,
      display_name: link.display_name,
      description: link.description || '',
      is_default: link.is_default
    });
    setShowAddForm(true);
  };

  const handleDelete = async (linkId) => {
    if (!window.confirm('Are you sure you want to delete this SharePoint link?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/products/${product.id}/sharepoint/${linkId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('exled_token')}`
        }
      });
      
      toast({
        title: "Success",
        description: "SharePoint link deleted successfully",
      });
      
      fetchSharepointLinks();
      onUpdate(); // Refresh parent component
      
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete SharePoint link",
        variant: "destructive",
      });
    }
  };

  const handleOpenSharePoint = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const resetForm = () => {
    setFormData({
      sharepoint_url: '',
      display_name: '',
      description: '',
      is_default: false
    });
    setErrors({});
    setEditingLink(null);
    setShowAddForm(false);
  };

  if (!isOpen || !product) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">
              SharePoint Management - {product.title}
            </h2>
            <p className="text-sm text-slate-600">
              Manage SharePoint links for this product
            </p>
          </div>
          <UIButton
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-slate-500 hover:text-slate-700"
          >
            <X className="w-5 h-5" />
          </UIButton>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {/* SharePoint Links Section */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">SharePoint Links</h3>
              <UIButton
                onClick={() => setShowAddForm(!showAddForm)}
                size="sm"
                className="flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                {showAddForm ? 'Cancel' : 'Add Link'}
              </UIButton>
            </div>

            {/* Add/Edit Form */}
            {showAddForm && (
              <div className="bg-slate-50 rounded-lg p-4 mb-6">
                <h4 className="font-medium text-slate-900 mb-4">
                  {editingLink ? 'Edit SharePoint Link' : 'Add New SharePoint Link'}
                </h4>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <UILabel htmlFor="sharepoint_url">SharePoint URL *</UILabel>
                    <UIInput
                      id="sharepoint_url"
                      type="url"
                      value={formData.sharepoint_url}
                      onChange={(e) => setFormData({...formData, sharepoint_url: e.target.value})}
                      placeholder="https://company.sharepoint.com/sites/product-docs"
                      className={errors.sharepoint_url ? 'border-red-500' : ''}
                    />
                    {errors.sharepoint_url && (
                      <p className="text-red-500 text-sm mt-1">{errors.sharepoint_url}</p>
                    )}
                  </div>

                  <div>
                    <UILabel htmlFor="display_name">Display Name *</UILabel>
                    <UIInput
                      id="display_name"
                      value={formData.display_name}
                      onChange={(e) => setFormData({...formData, display_name: e.target.value})}
                      placeholder="Product Documentation"
                      className={errors.display_name ? 'border-red-500' : ''}
                    />
                    {errors.display_name && (
                      <p className="text-red-500 text-sm mt-1">{errors.display_name}</p>
                    )}
                  </div>

                  <div>
                    <UILabel htmlFor="description">Description</UILabel>
                    <UITextarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      placeholder="Brief description of the SharePoint site contents..."
                      rows={3}
                    />
                  </div>

                  <div className="flex items-center space-x-2">
                    <UICheckbox
                      id="is_default"
                      checked={formData.is_default}
                      onCheckedChange={(checked) => setFormData({...formData, is_default: checked})}
                    />
                    <UILabel htmlFor="is_default" className="text-sm">
                      Set as default SharePoint link
                    </UILabel>
                  </div>

                  <div className="flex justify-end space-x-2">
                    <UIButton
                      type="button"
                      variant="outline"
                      onClick={resetForm}
                      disabled={loading}
                    >
                      Cancel
                    </UIButton>
                    <UIButton
                      type="submit"
                      disabled={loading}
                    >
                      {loading ? 'Saving...' : (editingLink ? 'Update Link' : 'Add Link')}
                    </UIButton>
                  </div>
                </form>
              </div>
            )}

            {/* Existing Links */}
            {sharepointLinks.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <ExternalLink className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No SharePoint links configured for this product</p>
                <p className="text-sm">Click "Add Link" to get started</p>
              </div>
            ) : (
              <div className="space-y-3">
                {sharepointLinks.map((link) => (
                  <div key={link.id} className="border border-slate-200 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-medium text-slate-900">{link.display_name}</h4>
                          {link.is_default && (
                            <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                              Default
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-600 mb-2">{link.description}</p>
                        <div className="flex items-center gap-4 text-xs text-slate-500">
                          <span>Created: {new Date(link.created_at).toLocaleDateString()}</span>
                          {link.updated_at !== link.created_at && (
                            <span>Updated: {new Date(link.updated_at).toLocaleDateString()}</span>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 ml-4">
                        <UIButton
                          variant="outline"
                          size="sm"
                          onClick={() => handleOpenSharePoint(link.sharepoint_url)}
                          className="flex items-center gap-2"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Open
                        </UIButton>
                        
                        <>
                          <UIButton
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(link)}
                            className="flex items-center gap-2"
                          >
                            <Edit className="w-4 h-4" />
                            Edit
                          </UIButton>
                          <UIButton
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(link.id)}
                            className="flex items-center gap-2 text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete
                          </UIButton>
                        </>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SharePointManagementModal;
